/**
 * Media Types
 * ============================================
 * Shared types for media uploads and per-upload AI analyses.
 */
import { Action } from "./Authorization.js";
/**
 * Entity types that own media files. Defined as a const object + derived type
 * so the resulting type is a string literal union — assignment-compatible with
 * the backend's MediaEntityType enum and existing frontend unions.
 */
export const MediaEntityType = {
    LivestockAnimal: "LivestockAnimal",
    GrassOptzPaddock: "GrassOptzPaddock",
    EquipMgmtEquipment: "EquipMgmtEquipment",
    /**
     * Farm-level anchor for documents that span many paddocks — the soil lab
     * report (Laudo) is one PDF holding several samples (glebas), so it binds to
     * the establishment, not a single paddock; the gleba→paddock mapping happens
     * at review time before the soil rows are created.
     */
    GrassOptzEstablishment: "GrassOptzEstablishment",
    /**
     * A financial transaction's nota/comprovante (receipt). Store-only — accepts
     * photo or PDF and runs no AI analysis, so its reason list is empty. Attaching
     * is always optional; the transaction can carry zero or many receipts.
     */
    FinancialHubTransaction: "FinancialHubTransaction",
    /**
     * Transient staging anchor for the receipt-OCR capture flow (NDC-108). A
     * nota/comprovante is uploaded and analysed here *before* any transaction
     * exists — the transaction is only created on confirm, at which point the
     * draft media is re-pointed onto it (becoming a normal
     * `FinancialHubTransaction` attachment). Org-scoped: the `entityId` is a
     * client-generated draft session id, not a persisted row, so nothing is
     * created in the ledger before the user confirms.
     */
    FinancialHubReceiptDraft: "FinancialHubReceiptDraft",
    /**
     * Anchor for one animal-list bulk import (NDC — animal bulk import). The
     * `entityId` is the establishment the animals will land on, and the media file
     * is the **canonical CSV** the client produced from the producer's `.csv` /
     * `.xlsx` / PDF / photo — so the retained artifact is exactly the text the
     * model read. Unlike every other entity type this one fans a single upload out
     * into up to `ANIMAL_IMPORT_MAX_CHUNKS` analyses of the same reason, which is
     * why the chunk index is part of the analysis row's identity.
     */
    LivestockAnimalImport: "LivestockAnimalImport",
};
/**
 * All media entity type values in a stable (declaration) order. Single source of
 * truth for every hand-written union that used to restate them — the backend
 * request schema, the S3 key path type, and the frontend upload flows all derive
 * from this so they can never drift apart.
 */
export const MEDIA_ENTITY_TYPES = Object.values(MediaEntityType);
/**
 * Media mime allowlists — the single source of truth shared by the backend
 * presign/validation path (`s3Service` re-exports these) and the frontend upload
 * pickers, so the two can never disagree on what the bucket accepts.
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];
export const ALLOWED_DOCUMENT_MIME_TYPES = ["application/pdf"];
/**
 * Plain-text mimes. Only the animal-list import accepts one, and only the
 * canonical CSV form (Decision C): the client decodes, re-serialises and
 * `__row`-stamps whatever the producer picked, so the bucket never holds a
 * spreadsheet binary or a Latin-1 file.
 */
export const ALLOWED_TEXT_MIME_TYPES = ["text/csv"];
/** Every mime the bucket accepts across all entity types. */
export const ALLOWED_MEDIA_MIME_TYPES = [
    ...ALLOWED_IMAGE_MIME_TYPES,
    ...ALLOWED_DOCUMENT_MIME_TYPES,
    ...ALLOWED_TEXT_MIME_TYPES,
];
/**
 * Default upload size cap in bytes, mirroring the backend `S3_MEDIA_MAX_BYTES`
 * env default. The backend env value is authoritative and enforced server-side;
 * the frontend uses this only for an early client-side reject.
 */
export const DEFAULT_MEDIA_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Entity types whose media may be a PDF document in addition to images — the
 * soil lab report (Laudo) and financial-transaction comprovantes. Single source
 * of truth shared by the backend mime allowlist and the frontend upload picker
 * so the two can never disagree on what a given entity accepts.
 */
export const MEDIA_DOCUMENT_ENTITY_TYPES = [
    MediaEntityType.GrassOptzEstablishment,
    MediaEntityType.FinancialHubTransaction,
    MediaEntityType.FinancialHubReceiptDraft,
    // A herd list is as often a photographed caderno page or a PDF as it is a
    // spreadsheet. Visual input is single-chunk (Decision E) — a line range is
    // meaningless for a PDF — so a long PDF is refused rather than split.
    MediaEntityType.LivestockAnimalImport,
];
export function entityTypeAllowsDocuments(entityType) {
    return MEDIA_DOCUMENT_ENTITY_TYPES.includes(entityType);
}
/**
 * Entity types whose media may be a plain-text upload (the canonical CSV) in
 * addition to images and PDFs. Deliberately narrow: a text upload is the only
 * media kind the worker *slices* before sending, so admitting one anywhere else
 * would also admit a chunk plan there.
 */
export const MEDIA_TEXT_ENTITY_TYPES = [
    MediaEntityType.LivestockAnimalImport,
];
export function entityTypeAllowsText(entityType) {
    return MEDIA_TEXT_ENTITY_TYPES.includes(entityType);
}
/**
 * Decision I — the ONE place an entity type's media permissions are stated.
 *
 * The route macros stay as the cheap coarse gate, but three of the five media
 * routes (`complete`, `analyses`, `retry`) are keyed by `mediaFileId`, so the
 * entity type is not knowable until the row is read: a route-level macro
 * structurally cannot express this rule. `mediaService` therefore asserts
 * against this table *after* loading the row, and that assert is the
 * authoritative one (defence in depth, not duplication).
 *
 * **Authoring rule.** An entity type whose domain owner is unambiguous is
 * tightened to that domain's actions. An entity type a second domain's UI
 * legitimately reads would keep the broader list **explicitly, with the reason
 * on its entry** — that arm has no members today: every entity type below is
 * read and written from exactly one module's screens, so each entry names its
 * owner and drops the other two domains. The arm is documented anyway because
 * the next entity type may need it, and a silent widening is the failure mode
 * this table exists to prevent.
 */
export const MEDIA_ENTITY_PERMISSIONS = {
    // Animal photos, read and written only from the livestock animal profile,
    // whose write routes already gate on MANAGE_LIVESTOCK. Tightened: an
    // equipment- or finance-only member has no reason to see an animal's photos.
    [MediaEntityType.LivestockAnimal]: {
        read: [Action.VIEW_FARMS],
        write: [Action.MANAGE_LIVESTOCK],
    },
    // Paddock photos, read and written only from the grass-production paddock
    // page. Tightened to the farm actions rather than VIEW_PADDOCKS /
    // MANAGE_PADDOCKS: those are not in the media macro vocabulary, so naming
    // them here would state an access path the coarse gate does not grant.
    [MediaEntityType.GrassOptzPaddock]: {
        read: [Action.VIEW_FARMS],
        write: [Action.MANAGE_FARMS],
    },
    // The soil lab report (Laudo). Farm-level anchor, read and written only from
    // the grass-production soil import flow. Tightened.
    [MediaEntityType.GrassOptzEstablishment]: {
        read: [Action.VIEW_FARMS],
        write: [Action.MANAGE_FARMS],
    },
    // Equipment photos, read and written only from the fleet screens. Tightened.
    [MediaEntityType.EquipMgmtEquipment]: {
        read: [Action.VIEW_EQUIPMENT],
        write: [Action.MANAGE_EQUIPMENT],
    },
    // A transaction's nota/comprovante — a financial document, and its download
    // URL is the document itself. Tightened to finance.
    [MediaEntityType.FinancialHubTransaction]: {
        read: [Action.VIEW_FINANCE],
        write: [Action.MANAGE_FINANCE],
    },
    // Receipt-OCR staging anchor. Same domain and same sensitivity as the
    // transaction attachment it becomes on confirm, so it carries the same pair.
    [MediaEntityType.FinancialHubReceiptDraft]: {
        read: [Action.VIEW_FINANCE],
        write: [Action.MANAGE_FINANCE],
    },
    // MANAGE_LIVESTOCK for BOTH modes, deliberately: there is no view-only use of
    // an import artifact. Reading it means receiving a presigned download URL for
    // the producer's whole herd list, and the read path also feeds the per-chunk
    // retry that re-spends extraction budget. Anyone allowed to look at it is
    // already allowed to create the animals it describes.
    [MediaEntityType.LivestockAnimalImport]: {
        read: [Action.MANAGE_LIVESTOCK],
        write: [Action.MANAGE_LIVESTOCK],
    },
};
// ============================================
// ANIMAL-LIST IMPORT UPLOAD CONTRACT
// ============================================
/**
 * How the row count travelling with an upload was obtained. `PARSED` is the
 * only authoritative source; the other two exist because a photographed caderno
 * page has no parseable count.
 */
export const ANIMAL_IMPORT_ROW_COUNT_SOURCES = [
    "PARSED",
    "DECLARED",
    "UNKNOWN",
];
/**
 * The discriminant ↔ nullability rule, checked at runtime. Scoped to that rule
 * on purpose: the mime rule (`PARSED` required for `text/csv`, forbidden for
 * image/PDF), the `UNKNOWN`-implies-single-chunk rule and the
 * `declaredRowCount === Σ(endLine − startLine)` rule all need context this
 * module does not have, and they live with the chunk-plan validation in
 * `mediaService` (Decision B).
 */
export function isAnimalImportRowCount(value) {
    if (value.rowCountSource === "UNKNOWN") {
        return value.declaredRowCount === null;
    }
    return (value.declaredRowCount !== null &&
        Number.isInteger(value.declaredRowCount) &&
        value.declaredRowCount > 0);
}
/**
 * Decision J — the synthetic ordinal column injected as the canonical CSV's
 * first column, holding each record's 1-based position in the whole file. The
 * model transcribes it into `sourceRow`; that is what makes coverage checkable
 * per chunk, because strict tool use constrains the output's *shape* and not
 * its *order*.
 */
export const ANIMAL_IMPORT_SOURCE_ROW_COLUMN = "__row";
export var MediaAnalysisReason;
(function (MediaAnalysisReason) {
    // LivestockAnimal
    MediaAnalysisReason["ANIMAL_BODY_CONDITION_SCORE"] = "ANIMAL_BODY_CONDITION_SCORE";
    MediaAnalysisReason["ANIMAL_LAMENESS_CHECK"] = "ANIMAL_LAMENESS_CHECK";
    MediaAnalysisReason["ANIMAL_HEALTH_INDICATORS"] = "ANIMAL_HEALTH_INDICATORS";
    MediaAnalysisReason["ANIMAL_BREED_IDENTIFICATION"] = "ANIMAL_BREED_IDENTIFICATION";
    // GrassOptzPaddock
    MediaAnalysisReason["PADDOCK_PASTURE_QUALITY"] = "PADDOCK_PASTURE_QUALITY";
    MediaAnalysisReason["PADDOCK_WEED_DETECTION"] = "PADDOCK_WEED_DETECTION";
    MediaAnalysisReason["PADDOCK_FENCE_CONDITION"] = "PADDOCK_FENCE_CONDITION";
    MediaAnalysisReason["PADDOCK_COVERAGE_ESTIMATION"] = "PADDOCK_COVERAGE_ESTIMATION";
    // EquipMgmtEquipment
    MediaAnalysisReason["EQUIPMENT_WEAR_CHECK"] = "EQUIPMENT_WEAR_CHECK";
    MediaAnalysisReason["EQUIPMENT_DAMAGE_DETECTION"] = "EQUIPMENT_DAMAGE_DETECTION";
    MediaAnalysisReason["EQUIPMENT_MAINTENANCE_INDICATORS"] = "EQUIPMENT_MAINTENANCE_INDICATORS";
    // GrassOptzEstablishment
    MediaAnalysisReason["SOIL_LAB_REPORT_EXTRACTION"] = "SOIL_LAB_REPORT_EXTRACTION";
    // FinancialHubReceiptDraft
    MediaAnalysisReason["TRANSACTION_RECEIPT_EXTRACTION"] = "TRANSACTION_RECEIPT_EXTRACTION";
    // LivestockAnimalImport
    MediaAnalysisReason["ANIMAL_LIST_IMPORT_EXTRACTION"] = "ANIMAL_LIST_IMPORT_EXTRACTION";
})(MediaAnalysisReason || (MediaAnalysisReason = {}));
export var MediaAnalysisStatus;
(function (MediaAnalysisStatus) {
    MediaAnalysisStatus["PENDING"] = "PENDING";
    MediaAnalysisStatus["IN_PROGRESS"] = "IN_PROGRESS";
    MediaAnalysisStatus["COMPLETED"] = "COMPLETED";
    MediaAnalysisStatus["FAILED"] = "FAILED";
})(MediaAnalysisStatus || (MediaAnalysisStatus = {}));
/** Maximum number of analyses that can be requested for a single upload. */
export const MAX_REASONS_PER_UPLOAD = 3;
/**
 * Reasons valid for each entity type. Single source of truth shared between
 * frontend (renders the picker) and backend (validates the request).
 */
export const REASONS_BY_ENTITY_TYPE = {
    [MediaEntityType.LivestockAnimal]: [
        MediaAnalysisReason.ANIMAL_BODY_CONDITION_SCORE,
        MediaAnalysisReason.ANIMAL_LAMENESS_CHECK,
        MediaAnalysisReason.ANIMAL_HEALTH_INDICATORS,
        MediaAnalysisReason.ANIMAL_BREED_IDENTIFICATION,
    ],
    [MediaEntityType.GrassOptzPaddock]: [
        MediaAnalysisReason.PADDOCK_PASTURE_QUALITY,
        MediaAnalysisReason.PADDOCK_WEED_DETECTION,
        MediaAnalysisReason.PADDOCK_FENCE_CONDITION,
        MediaAnalysisReason.PADDOCK_COVERAGE_ESTIMATION,
    ],
    [MediaEntityType.EquipMgmtEquipment]: [
        MediaAnalysisReason.EQUIPMENT_WEAR_CHECK,
        MediaAnalysisReason.EQUIPMENT_DAMAGE_DETECTION,
        MediaAnalysisReason.EQUIPMENT_MAINTENANCE_INDICATORS,
    ],
    [MediaEntityType.GrassOptzEstablishment]: [
        MediaAnalysisReason.SOIL_LAB_REPORT_EXTRACTION,
    ],
    // Comprovantes are stored as-is — no AI analysis reasons.
    [MediaEntityType.FinancialHubTransaction]: [],
    // Receipt-OCR staging: the one reason that extracts a nota into entry fields.
    [MediaEntityType.FinancialHubReceiptDraft]: [
        MediaAnalysisReason.TRANSACTION_RECEIPT_EXTRACTION,
    ],
    // The one reason that is requested N times for one upload — once per chunk.
    // The chunk plan travels as its own input, never as a repeated reason, because
    // MAX_REASONS_PER_UPLOAD and the duplicate-reason check both forbid that.
    [MediaEntityType.LivestockAnimalImport]: [
        MediaAnalysisReason.ANIMAL_LIST_IMPORT_EXTRACTION,
    ],
};
/** Human-readable label + short description per reason (UI copy). */
export const REASON_METADATA = {
    [MediaAnalysisReason.ANIMAL_BODY_CONDITION_SCORE]: {
        label: "Body condition score",
        description: "Estimate the animal's body condition score from the image.",
    },
    [MediaAnalysisReason.ANIMAL_LAMENESS_CHECK]: {
        label: "Lameness check",
        description: "Flag visible signs of lameness or gait issues.",
    },
    [MediaAnalysisReason.ANIMAL_HEALTH_INDICATORS]: {
        label: "Health indicators",
        description: "Detect visible health indicators (coat, eyes, posture).",
    },
    [MediaAnalysisReason.ANIMAL_BREED_IDENTIFICATION]: {
        label: "Breed identification",
        description: "Identify the most likely breed or breed mix.",
    },
    [MediaAnalysisReason.PADDOCK_PASTURE_QUALITY]: {
        label: "Pasture quality",
        description: "Assess pasture quality, density and colour.",
    },
    [MediaAnalysisReason.PADDOCK_WEED_DETECTION]: {
        label: "Weed detection",
        description: "Detect presence of visible weeds in the paddock.",
    },
    [MediaAnalysisReason.PADDOCK_FENCE_CONDITION]: {
        label: "Fence condition",
        description: "Assess visible condition of fences in the image.",
    },
    [MediaAnalysisReason.PADDOCK_COVERAGE_ESTIMATION]: {
        label: "Coverage estimation",
        description: "Estimate ground coverage percentage.",
    },
    [MediaAnalysisReason.EQUIPMENT_WEAR_CHECK]: {
        label: "Wear check",
        description: "Detect visible wear on the equipment.",
    },
    [MediaAnalysisReason.EQUIPMENT_DAMAGE_DETECTION]: {
        label: "Damage detection",
        description: "Detect visible damage on the equipment.",
    },
    [MediaAnalysisReason.EQUIPMENT_MAINTENANCE_INDICATORS]: {
        label: "Maintenance indicators",
        description: "Surface visible indicators that maintenance is needed.",
    },
    [MediaAnalysisReason.SOIL_LAB_REPORT_EXTRACTION]: {
        label: "Soil lab report extraction",
        description: "Extract the structured soil-analysis results from a lab report (PDF or photo), one entry per sample.",
    },
    [MediaAnalysisReason.TRANSACTION_RECEIPT_EXTRACTION]: {
        label: "Receipt extraction",
        description: "Extract amount, date, issuer and suggested category from a receipt/nota (PDF or photo) to pre-fill a manual entry.",
    },
    [MediaAnalysisReason.ANIMAL_LIST_IMPORT_EXTRACTION]: {
        label: "Animal list extraction",
        description: "Transcribe one chunk of an animal list (spreadsheet, PDF or photo) into structured animal rows for review before import.",
    },
};
export function isReasonValidForEntityType(entityType, reason) {
    return REASONS_BY_ENTITY_TYPE[entityType].includes(reason);
}
//# sourceMappingURL=Media.js.map